import React, { useState, useEffect } from "react";
import styled from "styled-components/macro";

import shuffle from "../utils/shuffle";
import split from "../utils/split";

const Container = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
`;

const WordList = styled.ul`
  padding: 0;
  list-style-type: none;

  font-size: 1.75rem;

  border: 1px solid #e9ecef;
  padding: 1.5rem 2rem;
  border-radius: 10px;

  box-sizing: border-box;

  @media screen and (max-width: 900px) {
    font-size: 1.2rem;
  }
`;

const WordItem = styled.li`
  display: inline-block;
  color: #343a40;

  transition: color 0.3s;

  &.used {
    color: #dee2e6;
  }

  & + &::before {
    content: "/";
    margin: 0 1rem;
    color: #ced4da;
  }
`;

const InputContainer = styled.div`
  display: flex;

  width: min(50rem, 100%);
`;

const AnswerInput = styled.textarea`
  font-family: "Noto Sans KR", sans-serif;
  font-size: 1.5rem;
  padding: 1rem 1.5rem;
  box-sizing: border-box;

  width: 100%;

  border: 1px solid #e9ecef;
  border-radius: 10px;
  background-color: #f8f9fa;

  outline: none;
  color: #343a40;

  flex: 1;

  transition: background-color 0.3s;
  &.correct {
    background-color: #c3fae8;
  }
`;

const SubmitButton = styled.button`
  cursor: pointer;

  width: 70px;
  margin-left: 1rem;
  font-size: 2rem;

  appearance: none;
  background-color: #f06595;
  border: none;
  border-radius: 10px;

  text-align: center;
`;

const MeaningText = styled.p`
  font-size: 2.5rem;
  color: #343a40;
  text-align: center;

  margin: 0 0 3.5rem 0;

  @media screen and (max-width: 900px) {
    font-size: 2rem;
    margin-bottom: 1rem;
  }
`;

const SentenceNumber = styled.h1`
  font-size: 3rem;
  margin: 0;

  text-align: center;
  font-size: 2rem;
  color: #495057;
  margin-bottom: 1rem;
`;

interface QuizProps {
  sentence: string;
  meaning: string;
  sentenceNumber: number;
  correctCount: number;

  onSubmit: (answer: string) => void;
}

export default function Quiz({
  sentence,
  meaning,
  sentenceNumber,
  correctCount,
  onSubmit,
}: QuizProps) {
  const [splited, setSplited] = useState<{ word: string; used: boolean }[]>([]);
  const [answer, setAnswer] = useState("");

  const [animate, setAnimate] = useState(false);

  useEffect(() => {
    if (correctCount === 0) return;

    setAnimate(true);
    const id = setTimeout(() => setAnimate(false), 1000);

    return () => {
      clearTimeout(id);
    };
  }, [correctCount]);

  useEffect(() => {
    setSplited(shuffle(split(sentence).map((word) => ({ word, used: false }))));
    setAnswer("");
  }, [sentence]);

  function onChange(event: React.ChangeEvent<HTMLTextAreaElement>) {
    setAnswer(event.target.value);

    const newInfoList = splited.map((i) => ({ ...i, used: false }));

    for (const inputWord of split(event.target.value)) {
      const i = newInfoList.findIndex(
        ({ word, used }) => !used && word === inputWord
      );

      if (i !== -1) {
        newInfoList[i].used = true;
      }
    }

    setSplited(newInfoList);
  }

  function onKeyDown(event: React.KeyboardEvent<HTMLTextAreaElement>) {
    if (event.key === "Enter") {
      event.preventDefault();
      onSubmit(answer);
    }
  }

  return (
    <Container>
      <SentenceNumber>{sentenceNumber}번 문장</SentenceNumber>
      <MeaningText>{meaning}</MeaningText>
      <WordList>
        {splited.map((wordInfo, i) => (
          <WordItem key={i} className={wordInfo.used ? "used" : ""}>
            {wordInfo.word}
          </WordItem>
        ))}
      </WordList>
      <InputContainer>
        <AnswerInput
          onChange={onChange}
          value={answer}
          onKeyDown={(e) => onKeyDown(e)}
          className={animate ? "correct" : ""}
        />
        <SubmitButton onClick={() => onSubmit(answer)}>👌</SubmitButton>
      </InputContainer>
    </Container>
  );
}
